import _find from 'lodash/find';
import _find_index from 'lodash/findIndex';
import axios from 'axios';
import Vue from 'vue';
import { request_mutations, request_state } from '../utils/store';
import {
    NOTES_DETAIL, NOTES_BOOKMARK, NOTES_LIST, NOTES_LOCK, EMPTY_TRASH, NOTES_DUPLICATE,
} from '../api';
import { insert, remove_by_id } from '../utils/data';
import { auth } from '../utils/requests';
import { copy } from '../utils/helpers';

export default {
    namespaced: true,
    state: {
        list: request_state(),
        create: request_state(),
        detail: request_state(),
        update: request_state(),
        bookmark: request_state(),
        remove: request_state(),
        move: request_state(),
        duplicate: request_state(),
        empty_trash: request_state(),
        lock: request_state(),
    },
    mutations: {
        ...request_mutations('notes_list', 'list'),
        ...request_mutations('create_note', 'create'),
        ...request_mutations('detail_note', 'detail'),
        ...request_mutations('update_note', 'update'),
        ...request_mutations('remove_note', 'remove'),
        ...request_mutations('bookmark_note', 'bookmark'),
        ...request_mutations('lock_note', 'lock'),
        ...request_mutations('move_note', 'move'),
        ...request_mutations('duplicate_note', 'duplicate'),
        ...request_mutations('empty_trash', 'empty_trash'),
        duplicate_note(state, { note_id, new_id, name }) {
            const index = _find_index(state.list.data.results, { id: note_id });
            if (index === -1) return;
            const note = copy(state.list.data.results[index]);
            note.id = new_id;
            note.name = name;
            Vue.set(state.list.data, 'results', insert(state.list.data.results, index, note));
        },
        remove_note(state, note_id) {
            state.list.data.results = remove_by_id(state.list.data.results, note_id);
        },
        clean_detail(state, id) {
            if (state.detail.data && id === state.detail.data.id) {
                state.detail = request_state();
            }
        },
        extend_list(state, data) {
            state.list.loading = false;
            state.list.error = {};
            state.list.data.results.push(...data.results);
            state.list.data.next = data.next;
        },
        add_note(state, note) {
            state.list.data.results.unshift(note);
        },
        clear_notes_list(state) {
            state.list = request_state();
        },
        add_attachment(state, attachment) {
            state.detail.data.attachments.unshift(attachment);
        },
        remove_attachment(state, attachment_id) {
            const { attachments } = state.detail.data;
            state.detail.data.attachments = remove_by_id(attachments, attachment_id);
        },
        rename_attachment(state, { attachment_id, name }) {
            const attachment = _find(state.detail.data.attachments, { id: attachment_id });
            Vue.set(attachment, 'name', name);
        },
        lock_note(state, is_locked) {
            state.detail.data.is_locked = is_locked;
        },
        bookmark_note(state) {
            state.detail.data.bookmarked = !state.detail.data.bookmarked;
        },
        update_note_name(state, { note_id, name }) {
            const note = _find(state.list.data.results, { id: note_id });
            if (note) {
                note.name = name;
            }
        },
        add_tag(state, tag) {
            state.detail.data.note_tags.push(tag);
        },
        remove_tag(state, id) {
            state.detail.data.note_tags = state.detail.data.note_tags.filter(obj => obj.id !== id);
        },
        change_note_topic(state, { note_id, topic }) {
            const note = _find(state.list.data.results, { id: note_id });
            if (note) {
                note.topic = topic;
            }
        },
        add_comments_count(state, add = 1) {
            const count = state.detail.data.comments_count + add;
            Vue.set(state.detail.data, 'comments_count', count);
        },
        set_as_read(state, note_id) {
            if (!state.list.data) return;
            const note = _find(state.list.data.results, { id: note_id });
            if (note) note.is_unread = false;
        },
    },
    actions: {
        list({ commit }, {
            space_id, extend, has_not_card, search, name_contains, hide_private,
            content_contains, is_private, is_bookmarked, is_trash, is_unread,
            tags, authors, topics, page, or_tags, ordering = '-created_at',
        }) {
            const url = NOTES_LIST.replace('{space_id}', space_id);
            const params = {
                page: page || 1,
                content_contains,
                name_contains,
                has_not_card,
                hide_private,
                ordering,
                or_tags,
                authors,
                search,
                topics,
                tags,
            };

            if (is_trash) params.trash = true;
            if (is_private) params.private = true;
            if (is_bookmarked) params.bookmarked = true;
            if (is_unread) params.unread = true;

            commit('notes_list_loading');
            return new Promise((resolve) => {
                axios.get(url, { params, ...auth() }).then((response) => {
                    commit(extend ? 'extend_list' : 'notes_list_success', response.data);
                    resolve(response.data);
                }).catch((error) => {
                    commit('notes_list_error', error.response ? error.response.data : {});
                });
            });
        },
        create({ commit }, { space_id, data }) {
            const url = NOTES_LIST.replace('{space_id}', space_id);

            commit('create_note_loading');
            return new Promise((resolve) => {
                axios.post(url, data, auth()).then((response) => {
                    commit('create_note_success', response.data);
                    commit('detail_note_success', response.data);
                    resolve(response.data);
                }).catch((error) => {
                    commit('create_note_error', error.response ? error.response.data : {});
                });
            });
        },
        detail({ commit }, { space_id, note_id }) {
            const url = NOTES_DETAIL.replace('{space_id}', space_id).replace('{note_id}', note_id);

            commit('detail_note_loading');
            return new Promise((resolve) => {
                axios.get(url, auth()).then((response) => {
                    commit('detail_note_success', response.data);
                    resolve(response.data);
                }).catch((error) => {
                    commit('detail_note_error', error.response ? error.response.data : {});
                });
            });
        },
        duplicate({ commit }, {
            space_id, note_id, is_tree_sidebar, data,
        }) {
            const url = NOTES_DUPLICATE
                .replace('{space_id}', space_id)
                .replace('{note_id}', note_id);

            commit('duplicate_note_loading');
            return new Promise((resolve) => {
                axios.post(url, data, auth()).then((response) => {
                    commit('duplicate_note_success', response.data);

                    commit(
                        is_tree_sidebar ? 'tree/duplicate_note' : 'notes/duplicate_note',
                        { note_id, new_id: response.data.id, name: response.data.name },
                        { root: true },
                    );

                    resolve(response.data);
                }).catch((error) => {
                    commit('duplicate_note_error', error.response ? error.response.data : {});
                });
            });
        },
        update({ commit }, {
            space_id,
            note_id,
            data,
            patch,
            update_detail = false,
        }) {
            const url = NOTES_DETAIL.replace('{space_id}', space_id).replace('{note_id}', note_id);

            commit('update_note_loading');
            return new Promise((resolve) => {
                axios[patch ? 'patch' : 'put'](url, data, auth()).then((response) => {
                    commit('update_note_success', response.data);
                    if (update_detail) {
                        commit('detail_note_success', response.data);
                    }
                    resolve(response.data);
                }).catch((error) => {
                    commit('update_note_error', error.response ? error.response.data : {});
                });
            });
        },
        lock({ commit }, { space_id, note_id, is_locked }) {
            const url = NOTES_LOCK.replace('{space_id}', space_id).replace('{note_id}', note_id);

            commit('lock_note_loading');
            axios.put(url, { is_locked }, auth()).then((response) => {
                commit('lock_note_success', response.data);
                commit('lock_note', is_locked);
            }).catch(error => commit('lock_note_error', error.response ? error.response.data : {}));
        },
        bookmark({ commit }, { space_id, note_id, remove }) {
            const url = NOTES_BOOKMARK
                .replace('{space_id}', space_id)
                .replace('{note_id}', note_id);

            commit('bookmark_note_loading');
            return new Promise((resolve) => {
                const request = remove ? axios.delete(url, auth()) : axios.post(url, {}, auth());

                request.then((response) => {
                    commit('bookmark_note_success', response.data);
                    commit('bookmark_note');
                    resolve(response.data);
                }).catch((error) => {
                    commit('bookmark_note_error', error.response ? error.response.data : {});
                });
            });
        },
        remove({ commit }, { space_id, note_id }) {
            const url = NOTES_DETAIL.replace('{space_id}', space_id).replace('{note_id}', note_id);

            commit('remove_note_loading');
            return new Promise((resolve) => {
                axios.delete(url, auth()).then((response) => {
                    commit('remove_note_success', response.data);
                    resolve();
                }).catch((error) => {
                    commit('remove_note_error', error.response ? error.response.data : {});
                });
            });
        },
        empty_trash({ commit }, { space_id }) {
            const url = EMPTY_TRASH.replace('{space_id}', space_id);

            commit('empty_trash_loading');
            return new Promise((resolve) => {
                axios.delete(url, auth()).then((response) => {
                    commit('empty_trash_success', response.data);
                    resolve();
                }).catch((error) => {
                    commit('empty_trash_error', error.response ? error.response.data : {});
                });
            });
        },
    },
};
